import PropTypes from "prop-types";
import React, { useEffect, Suspense, lazy } from "react";
import ScrollToTop from "./helpers/scroll-top";
import {
  Router,
  Switch,
  Route,
  Redirect,
  useLocation,
} from "react-router-dom";
import { ToastProvider } from "react-toast-notifications";
import { multilanguage, loadLanguages } from "redux-multilanguage";
import { connect, useDispatch } from "react-redux";
import { BreadcrumbsProvider } from "react-breadcrumbs-dynamic";
import { fetchBookmarks } from "./redux/actions/bookmarkActions";
import { loadDrafts } from "./redux/actions/fabricGroupActions";
import history from "./history";

// fabric
const Products = lazy(() => import("./pages/fabric/Products"));
const Home = lazy(() => import("./pages/fabric/Home"));
const Product = lazy(() => import("./pages/fabric/Product"));
const MillProducts = lazy(() => import("./pages/fabric/MillProducts"));
const BannerProducts = lazy(() => import("./pages/fabric/BannerProducts"));

// home pages
const HomeFashion = lazy(() => import("./pages/home/HomeFashion"));
const HomeFashionTwo = lazy(() => import("./pages/home/HomeFashionTwo"));
const HomeFashionThree = lazy(() => import("./pages/home/HomeFashionThree"));
const HomeFashionFour = lazy(() => import("./pages/home/HomeFashionFour"));
const HomeFashionFive = lazy(() => import("./pages/home/HomeFashionFive"));
const HomeFashionSix = lazy(() => import("./pages/home/HomeFashionSix"));
const HomeFashionSeven = lazy(() => import("./pages/home/HomeFashionSeven"));
const HomeFashionEight = lazy(() => import("./pages/home/HomeFashionEight"));
const HomeKidsFashion = lazy(() => import("./pages/home/HomeKidsFashion"));
const HomeCosmetics = lazy(() => import("./pages/home/HomeCosmetics"));
const HomeFurniture = lazy(() => import("./pages/home/HomeFurniture"));
const HomeFurnitureTwo = lazy(() => import("./pages/home/HomeFurnitureTwo"));
const HomeFurnitureThree = lazy(() =>
  import("./pages/home/HomeFurnitureThree")
);
const HomeFurnitureFour = lazy(() => import("./pages/home/HomeFurnitureFour"));
const HomeFurnitureFive = lazy(() => import("./pages/home/HomeFurnitureFive"));
const HomeFurnitureSix = lazy(() => import("./pages/home/HomeFurnitureSix"));
const HomeFurnitureSeven = lazy(() =>
  import("./pages/home/HomeFurnitureSeven")
);
const HomeElectronics = lazy(() => import("./pages/home/HomeElectronics"));
const HomeElectronicsTwo = lazy(() =>
  import("./pages/home/HomeElectronicsTwo")
);
const HomeElectronicsThree = lazy(() =>
  import("./pages/home/HomeElectronicsThree")
);
const HomeBookStore = lazy(() => import("./pages/home/HomeBookStore"));
const HomeBookStoreTwo = lazy(() => import("./pages/home/HomeBookStoreTwo"));
const HomePlants = lazy(() => import("./pages/home/HomePlants"));
const HomeFlowerShop = lazy(() => import("./pages/home/HomeFlowerShop"));
const HomeFlowerShopTwo = lazy(() => import("./pages/home/HomeFlowerShopTwo"));
const HomeOrganicFood = lazy(() => import("./pages/home/HomeOrganicFood"));
const HomeOrganicFoodTwo = lazy(() =>
  import("./pages/home/HomeOrganicFoodTwo")
);
const HomeOnepageScroll = lazy(() => import("./pages/home/HomeOnepageScroll"));
const HomeGridBanner = lazy(() => import("./pages/home/HomeGridBanner"));
const HomeAutoParts = lazy(() => import("./pages/home/HomeAutoParts"));
const HomeCakeShop = lazy(() => import("./pages/home/HomeCakeShop"));
const HomeHandmade = lazy(() => import("./pages/home/HomeHandmade"));
const HomePetFood = lazy(() => import("./pages/home/HomePetFood"));
const HomeMedicalEquipment = lazy(() =>
  import("./pages/home/HomeMedicalEquipment")
);
const HomeChristmas = lazy(() => import("./pages/home/HomeChristmas"));
const HomeBlackFriday = lazy(() => import("./pages/home/HomeBlackFriday"));
const HomeBlackFridayTwo = lazy(() =>
  import("./pages/home/HomeBlackFridayTwo")
);
const HomeValentinesDay = lazy(() => import("./pages/home/HomeValentinesDay"));

// shop pages
const ShopGridStandard = lazy(() => import("./pages/shop/ShopGridStandard"));
const ShopGridFilter = lazy(() => import("./pages/shop/ShopGridFilter"));
const ShopGridTwoColumn = lazy(() => import("./pages/shop/ShopGridTwoColumn"));
const ShopGridNoSidebar = lazy(() => import("./pages/shop/ShopGridNoSidebar"));
const ShopGridFullWidth = lazy(() => import("./pages/shop/ShopGridFullWidth"));
const ShopGridRightSidebar = lazy(() =>
  import("./pages/shop/ShopGridRightSidebar")
);
const ShopListStandard = lazy(() => import("./pages/shop/ShopListStandard"));
const ShopListFullWidth = lazy(() => import("./pages/shop/ShopListFullWidth"));
const ShopListTwoColumn = lazy(() => import("./pages/shop/ShopListTwoColumn"));

// product pages
// const Product = lazy(() => import("./pages/shop-product/Product"));
const ProductTabLeft = lazy(() =>
  import("./pages/shop-product/ProductTabLeft")
);
const ProductTabRight = lazy(() =>
  import("./pages/shop-product/ProductTabRight")
);
const ProductSticky = lazy(() => import("./pages/shop-product/ProductSticky"));
const ProductSlider = lazy(() => import("./pages/shop-product/ProductSlider"));
const ProductFixedImage = lazy(() =>
  import("./pages/shop-product/ProductFixedImage")
);

// blog pages
const BlogStandard = lazy(() => import("./pages/blog/BlogStandard"));
const BlogNoSidebar = lazy(() => import("./pages/blog/BlogNoSidebar"));
const BlogRightSidebar = lazy(() => import("./pages/blog/BlogRightSidebar"));
const BlogDetailsStandard = lazy(() =>
  import("./pages/blog/BlogDetailsStandard")
);

// other pages
const About = lazy(() => import("./pages/other/About"));
const StoreLocation = lazy(() => import("./pages/other/StoreLocation"));
const Contact = lazy(() => import("./pages/other/Contact"));
const MyAccount = lazy(() => import("./pages/other/MyAccount"));
const LoginRegister = lazy(() => import("./pages/other/LoginRegister"));

const Cart = lazy(() => import("./pages/other/Cart"));
const Wishlist = lazy(() => import("./pages/other/Wishlist"));
const Bookmarks = lazy(() => import("./pages/other/Bookmarks"));
const FabricGroups = lazy(() => import("./pages/other/FabricGroups"));
const SharedGroup = lazy(() => import("./pages/other/SharedGroup"));
const Compare = lazy(() => import("./pages/other/Compare"));
const Checkout = lazy(() => import("./pages/other/Checkout"));
const FAQs = lazy(() => import("./pages/other/FAQs"));
const PrivacyPolicy = lazy(() => import("./pages/other/PrivacyPolicy"));
const CommodityTrends = lazy(() => import("./pages/other/CommodityTrends"));

const NotFound = lazy(() => import("./pages/other/NotFound"));

const LOGIN_PATH = process.env.PUBLIC_URL + "/login-register";

// Paths reachable without being logged in. Everything else needs a token.
const PUBLIC_PATHS = [
  LOGIN_PATH,
  process.env.PUBLIC_URL + "/privacy-policy",
  process.env.PUBLIC_URL + "/commodity",
  process.env.PUBLIC_URL + "/commodity-trends",
];

// Shared group links still require a login, but they are deep links that are
// always opened in a fresh tab, so the two fresh-tab rules below have to make
// an exception for them or the recipient never reaches the list.
const SHARE_PATH_PREFIX = process.env.PUBLIC_URL + "/shared-group/";

// Gate for the whole app: nothing renders until the user is logged in.
const AuthGate = ({ children }) => {
  const location = useLocation();
  const token = localStorage.getItem("token");
  const isPublicPath = PUBLIC_PATHS.some(
    (path) => location.pathname === path || location.pathname === path + "/"
  );
  // PUBLIC_PATHS matches exactly, which a parameterised path can never do.
  const isShareLink = location.pathname.startsWith(SHARE_PATH_PREFIX);

  const homePath = process.env.PUBLIC_URL + "/";
  const isAtHome =
    location.pathname === homePath ||
    location.pathname === (process.env.PUBLIC_URL || "");

  // Detect newly opened or reopened browser tab
  const isSessionActive = sessionStorage.getItem("tabSessionActive");
  if (!isSessionActive) {
    sessionStorage.setItem("tabSessionActive", "true");
    localStorage.removeItem("product");
    sessionStorage.removeItem("product");

    // If user is logged in and reopens tab on a deep page, land on Home --
    // except on a share link, which is only ever opened in a fresh tab.
    if (token && !isAtHome && !isPublicPath && !isShareLink) {
      return <Redirect to={homePath} />;
    }
  }

  // Not logged in and asking for a private page -> send to login,
  // remembering where they were headed (or home if fresh tab).
  if (!token && !isPublicPath) {
    return (
      <Redirect
        to={{
          pathname: LOGIN_PATH,
          // A share link must survive the login round-trip even on a fresh tab.
          state: {
            from:
              isSessionActive || isShareLink
                ? location.pathname + location.search
                : homePath,
          },
        }}
      />
    );
  }

  // Already logged in -> no reason to sit on the login screen.
  if (token && location.pathname.startsWith(LOGIN_PATH)) {
    return <Redirect to={homePath} />;
  }

  return children;
};

// Custom component to handle authentication-based routing
const AuthRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const userPrivileges = localStorage.getItem("userPrivileges");

  // If no token, redirect to login
  if (!token) {
    return <Redirect to={process.env.PUBLIC_URL + "/login-register"} />;
  }

  // If user is basic_user, redirect to home page
  if (userPrivileges === "basic_user") {
    return <Redirect to={process.env.PUBLIC_URL + "/"} />;
  }

  // If token exists and user is not basic_user, allow access to the protected route
  return children;
};

// Custom component for routes that require both token and millId
const MillAuthRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const millId = localStorage.getItem("millId");

  // If no token, redirect to login
  if (!token) {
    return <Redirect to={process.env.PUBLIC_URL + "/login-register"} />;
  }

  // If token exists but no millId, redirect to home
  if (token && !millId) {
    return <Redirect to={process.env.PUBLIC_URL + "/"} />;
  }

  // If both token and millId exist, allow access
  return children;
};

// Custom component for root path handling
const RootRoute = () => {
  const token = localStorage.getItem("token");
  const millId = localStorage.getItem("millId");

  // If no token, show home
  if (!token) {
    return <Home />;
  }

  // If token exists but no millId, show home
  if (token && !millId) {
    return <Home />;
  }

  // If both token and millId exist, redirect to mill products
  if (token && millId) {
    return <Redirect to={process.env.PUBLIC_URL + "/mill"} />;
  }

  return <Home />;
};

function App(props) {
  const dispatch = useDispatch();

  const token = localStorage.getItem("token");
  if (!token && localStorage.getItem("userPrivileges") !== "basic_user") {
    localStorage.setItem("userPrivileges", "basic_user");
  }

  useEffect(() => {
    dispatch(
      loadLanguages({
        languages: {
          en: require("./translations/english.json"),
          fn: require("./translations/french.json"),
          de: require("./translations/germany.json"),
        },
      })
    );

    // Fetch bookmarks when app initializes if user is authenticated and not a basic_user
    const token = localStorage.getItem("token");
    const userPrivileges = localStorage.getItem("userPrivileges");
    if (token && userPrivileges !== "basic_user") {
      dispatch(fetchBookmarks());
    }

    // localStorage is the source of truth for group drafts; this also clears
    // any transient flags rehydrated by redux-localstorage-simple.
    dispatch(loadDrafts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <ToastProvider placement="bottom-left">
      <BreadcrumbsProvider>
        <Router history={history}>
          <ScrollToTop>
            <Suspense
              fallback={
                <div className="flone-preloader-wrapper">
                  <div className="flone-preloader">
                    <span></span>
                    <span></span>
                  </div>
                </div>
              }
            >
              <AuthGate>
              <Switch>
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/"}
                  component={RootRoute}
                />

                {/* Fabrics */}
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/products/stocks"}
                  component={Products}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/products"}
                  component={Products}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/mill"}
                  render={(props) => (
                    <MillAuthRoute>
                      <MillProducts {...props} />
                    </MillAuthRoute>
                  )}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/banner-products/:bannerId"}
                  component={BannerProducts}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/products/:name"}
                  component={Products}
                />
                {/* <Route
                  path={process.env.PUBLIC_URL + "/product/:id"}
                  component={Product}
                /> */}
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/product/:name/:id"}
                  render={(props) => (
                    <AuthRoute>
                      <Product {...props} />
                    </AuthRoute>
                  )}
                />
                <Route
                  exact
                  path={process.env.PUBLIC_URL + "/product/:category/:name/:id"}
                  render={(props) => (
                    <AuthRoute>
                      <Product {...props} />
                    </AuthRoute>
                  )}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/login-register"}
                  component={LoginRegister}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/bookmarks"}
                  render={(props) => (
                    <AuthRoute>
                      <Bookmarks {...props} />
                    </AuthRoute>
                  )}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/fabric-groups"}
                  render={(props) => (
                    <AuthRoute>
                      <FabricGroups {...props} />
                    </AuthRoute>
                  )}
                />
                {/* Share target: a plain Route inside AuthGate already means
                    "any logged-in user, any role". */}
                <Route
                  path={process.env.PUBLIC_URL + "/shared-group/:id"}
                  component={SharedGroup}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/faqs"}
                  component={FAQs}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/privacy-policy"}
                  component={PrivacyPolicy}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/about"}
                  component={About}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/store-location"}
                  component={StoreLocation}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/commodity"}
                  component={CommodityTrends}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/commodity-trends"}
                  component={CommodityTrends}
                />

                {/* <Route
                  path={process.env.PUBLIC_URL + "/home-furniture-four"}
                  component={HomeFurnitureFour}
                /> */}
                {/* Homepages
                <Route
                  path={process.env.PUBLIC_URL + "/home-fashion"}
                  component={HomeFashion}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-fashion-two"}
                  component={HomeFashionTwo}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-fashion-three"}
                  component={HomeFashionThree}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-fashion-four"}
                  component={HomeFashionFour}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-fashion-five"}
                  component={HomeFashionFive}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-fashion-six"}
                  component={HomeFashionSix}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-fashion-seven"}
                  component={HomeFashionSeven}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-fashion-eight"}
                  component={HomeFashionEight}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-kids-fashion"}
                  component={HomeKidsFashion}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-cosmetics"}
                  component={HomeCosmetics}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-furniture"}
                  component={HomeFurniture}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-furniture-two"}
                  component={HomeFurnitureTwo}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-furniture-three"}
                  component={HomeFurnitureThree}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-furniture-four"}
                  component={HomeFurnitureFour}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-furniture-five"}
                  component={HomeFurnitureFive}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-furniture-six"}
                  component={HomeFurnitureSix}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-furniture-seven"}
                  component={HomeFurnitureSeven}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-electronics"}
                  component={HomeElectronics}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-electronics-two"}
                  component={HomeElectronicsTwo}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-electronics-three"}
                  component={HomeElectronicsThree}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-book-store"}
                  component={HomeBookStore}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-book-store-two"}
                  component={HomeBookStoreTwo}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-plants"}
                  component={HomePlants}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-flower-shop"}
                  component={HomeFlowerShop}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-flower-shop-two"}
                  component={HomeFlowerShopTwo}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-organic-food"}
                  component={HomeOrganicFood}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-organic-food-two"}
                  component={HomeOrganicFoodTwo}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-onepage-scroll"}
                  component={HomeOnepageScroll}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-grid-banner"}
                  component={HomeGridBanner}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-auto-parts"}
                  component={HomeAutoParts}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-cake-shop"}
                  component={HomeCakeShop}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-handmade"}
                  component={HomeHandmade}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-pet-food"}
                  component={HomePetFood}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-medical-equipment"}
                  component={HomeMedicalEquipment}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-christmas"}
                  component={HomeChristmas}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-black-friday"}
                  component={HomeBlackFriday}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-black-friday-two"}
                  component={HomeBlackFridayTwo}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/home-valentines-day"}
                  component={HomeValentinesDay}
                />

                {/* Shop pages */}
                {/* <Route
                  path={process.env.PUBLIC_URL + "/shop-grid-standard"}
                  component={ShopGridStandard}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/shop-grid-filter"}
                  component={ShopGridFilter}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/shop-grid-two-column"}
                  component={ShopGridTwoColumn}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/shop-grid-no-sidebar"}
                  component={ShopGridNoSidebar}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/shop-grid-full-width"}
                  component={ShopGridFullWidth}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/shop-grid-right-sidebar"}
                  component={ShopGridRightSidebar}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/shop-list-standard"}
                  component={ShopListStandard}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/shop-list-full-width"}
                  component={ShopListFullWidth}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/shop-list-two-column"}
                  component={ShopListTwoColumn}
                /> */}

                {/* Shop product pages */}
                {/* <Route
                  path={process.env.PUBLIC_URL + "/product/:id"}
                  render={(routeProps) => (
                    <Product {...routeProps} key={routeProps.match.params.id} />
                  )}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/product-tab-left/:id"}
                  component={ProductTabLeft}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/product-tab-right/:id"}
                  component={ProductTabRight}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/product-sticky/:id"}
                  component={ProductSticky}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/product-slider/:id"}
                  component={ProductSlider}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/product-fixed-image/:id"}
                  component={ProductFixedImage}
                /> */}

                {/* Blog pages */}
                {/* <Route
                  path={process.env.PUBLIC_URL + "/blog-standard"}
                  component={BlogStandard}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/blog-no-sidebar"}
                  component={BlogNoSidebar}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/blog-right-sidebar"}
                  component={BlogRightSidebar}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/blog-details-standard"}
                  component={BlogDetailsStandard}
                /> */}

                {/* Other pages */}
                {/* <Route
                  path={process.env.PUBLIC_URL + "/about"}
                  component={About}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/contact"}
                  component={Contact}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/my-account"}
                  component={MyAccount}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/login-register"}
                  component={LoginRegister}
                />

                <Route
                  path={process.env.PUBLIC_URL + "/cart"}
                  component={Cart}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/wishlist"}
                  component={Wishlist}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/compare"}
                  component={Compare}
                />
                <Route
                  path={process.env.PUBLIC_URL + "/checkout"}
                  component={Checkout}
                />  */}

                <Route
                  path={process.env.PUBLIC_URL + "/not-found"}
                  component={NotFound}
                />

                <Route exact component={NotFound} />
              </Switch>
              </AuthGate>
            </Suspense>
          </ScrollToTop>
        </Router>
      </BreadcrumbsProvider>
    </ToastProvider>
  );
}

App.propTypes = {
  dispatch: PropTypes.func,
};

export default connect()(multilanguage(App));
